// app/login/page.tsx
"use client";

import { useRouter, useSearchParams } from "next/navigation";
import React, { Suspense, useEffect, useState } from "react";

import { SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { isPublicSignupEnabled } from "@/lib/signupFlag";

import { AuthForm } from "../auth/authForm";
import { useAuthStore } from "../auth/services_and_hooks/authStore";

/**
 * The main content of the login page.
 * Uses Zustand store directly instead of useAuthHook to avoid infinite loops.
 */
function LoginPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  // Get auth state directly from store, not from hook
  const { isAuthenticated } = useAuthStore();

  // This effect handles redirecting users who are ALREADY logged in.
  useEffect(() => {
    // Give a moment for the store to initialize
    const timer = setTimeout(() => {
      setHasCheckedAuth(true);

      const redirectedFrom = searchParams.get("redirectedFrom");

      if (isAuthenticated) {
        console.log(
          "[LoginPage] User is already authenticated. Resolving B2B destination...",
        );
        const destination = new URL("/auth/post-login", window.location.origin);
        if (redirectedFrom) {
          destination.searchParams.set("redirectedFrom", redirectedFrom);
        }
        router.replace(`${destination.pathname}${destination.search}`);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isAuthenticated, router, searchParams]);

  // This effect handles displaying error/success messages from the URL.
  useEffect(() => {
    const errorParam = searchParams.get("error");
    const messageParam = searchParams.get("message");
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
    } else if (messageParam) {
      setNotice(decodeURIComponent(messageParam));
    }
    document.title = "Login | howtheF.ai";
  }, [searchParams]);

  // Show loading only if:
  // 1. We haven't checked auth yet, OR
  // 2. User is authenticated (and will be redirected)
  if (!hasCheckedAuth || isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-y-2 border-gray-900" />
        <p className="mt-4 text-center text-gray-600">
          {isAuthenticated ? "Redirecting..." : "Loading..."}
        </p>
      </div>
    );
  }

  // Render the login form for unauthenticated users
  return (
    <>
      <SiteHeader hideGetStarted />
      <div className="min-h-screen bg-gray-50">
        {/* Header Section - Full Width */}
        <div className="text-center pt-12 pb-6 px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 leading-tight max-w-4xl mx-auto">
            Welcome back
          </h1>

          {/* <p className="text-base text-gray-600 max-w-lg mx-auto">
            Sign in to access your AI workflows
          </p> */}
        </div>

        {/* Form Container - Constrained Width */}
        <div className="max-w-md mx-auto px-4 pb-12 sm:px-6 lg:px-8">
          {error && (
            <div className="mb-6">
              <div className="bg-red-50 text-red-700 border border-red-200 p-3 rounded-lg text-sm">
                {error}
              </div>
            </div>
          )}
          {notice && (
            <div className="mb-6 rounded-lg bg-beige-dark p-3 text-sm text-text">
              {notice}
            </div>
          )}

          {/* Login Form */}
          <div className="bg-white rounded-lg shadow-sm border-t-8 border-secondary p-8">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Login</h3>
            </div>

            {(() => {
              const redirectedFrom =
                searchParams.get("redirectedFrom") || undefined;
              console.log(
                `[LoginPage] Passing redirectedFrom to AuthForm: ${redirectedFrom}`,
              );
              return (
                <AuthForm view="sign_in" redirectedFrom={redirectedFrom} />
              );
            })()}
          </div>

          {isPublicSignupEnabled() ? (
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Don&apos;t have an account?{" "}
                <a
                  href="/signup"
                  className="font-medium text-blue-600 hover:text-blue-700"
                >
                  Sign up for free
                </a>
              </p>
            </div>
          ) : (
            <p className="mt-6 text-center text-sm text-gray-600">
              Access is provisioned for howtheF.ai staff and client teams.
            </p>
          )}
        </div>
      </div>
      <SiteFooter />
    </>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-y-2 border-gray-900" />
          <p className="mt-4 text-center text-gray-600">Loading...</p>
        </div>
      }
    >
      <LoginPageContent />
    </Suspense>
  );
}

// v1
// // app/login/page.tsx
// 'use client';

// import { useRouter, useSearchParams } from 'next/navigation';
// import React, { Suspense, useEffect, useState } from 'react';
// import { SiteHeader } from '@/components/site-header';
// import { SiteFooter } from '@/components/site-footer';

// import { authService } from '@/app/auth/services_and_hooks/authService';
// import { useAuthStore } from '@/app/auth/services_and_hooks/authStore';
// import { useAuthHook as useAuth } from '@/app/auth/services_and_hooks/useAuthHook';

// // Keep logDebug if used, otherwise remove
// import { AuthForm } from '../auth/authForm'; // Adjusted import path

// function LoginPageContent() {
//   const router = useRouter();
//   const searchParams = useSearchParams();
//   const [error, setError] = useState<string | null>(null);
//   const { user, isLoading } = useAuth();
//   const { isAuthenticated: storeIsAuthenticated, isLoading: storeIsLoading } = useAuthStore(); // Removed resetState if not used
//   const [redirecting, setRedirecting] = useState(false);
//   const [loadingTimeout, setLoadingTimeout] = useState(false);

//   // Set a timeout to force show the login form after a short delay
//   useEffect(() => {
//     const timer = setTimeout(() => {
//       setLoadingTimeout(true);
//     }, 1000);

//     return () => clearTimeout(timer);
//   }, []);

//   // Handle redirects based on auth state
//   useEffect(() => {
//     const isLogout = searchParams.get('logout') === 'success' ||
//                      searchParams.get('force_reset') === 'true';
//     if (isLogout) return;

//     const effectiveIsAuthenticated = (!!user || storeIsAuthenticated) && !isLoading && !storeIsLoading;

//     if (effectiveIsAuthenticated && !redirecting) {
//       console.log('Auth detected, preparing redirect...');
//       setRedirecting(true);

//       const hasCompletedOnboarding = user?.user_metadata?.onboarding_complete ||
//         useAuthStore.getState().hasCompletedOnboarding ||
//         useAuthStore.getState().profile?.user_onboarding_complete;

//       const redirectTo = hasCompletedOnboarding
//         ? (searchParams.get('redirectedFrom') || '/ai-workflows')
//         : '/onboarding/basic-info'; // Redirect to onboarding start

//       console.log(`Redirecting to: ${redirectTo}`);

//       // Use window.location for potentially more reliable redirect after state update
//       setTimeout(() => {
//         window.location.href = redirectTo;
//       }, 50); // Short delay
//     }
//   }, [user, storeIsAuthenticated, isLoading, storeIsLoading, router, searchParams, redirecting]);

//   // Handle URL parameters and page title
//   useEffect(() => {
//     if (searchParams.get('force_reset') === 'true') {
//       setError('Auth state has been reset. Please sign in again.');
//     } else {
//       const errorParam = searchParams.get('error');
//       if (errorParam) setError(decodeURIComponent(errorParam));
//     }

//     if (searchParams.get('logout') === 'success') {
//       setError('You have been successfully signed out.');
//     }

//     document.title = 'Login | howtheF.ai';
//   }, [searchParams]);

//   // Handle emergency auth reset
//   const handleEmergencyReset = async () => {
//     try {
//       setError('Attempting emergency auth reset...');
//       await authService.forceSignOut();
//     } catch (error) {
//       console.error('Emergency reset error:', error);
//       setError('Failed to reset auth state. Please try clearing cookies manually.');
//     }
//   };

//   // Determine if we should show login form
//   const shouldShowLoginForm = loadingTimeout || (!isLoading && !storeIsLoading);

//   // Loading state
//   if (!shouldShowLoginForm && !redirecting) {
//     return (
//       <>
//         <SiteHeader hideGetStarted />
//         <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
//           <div className="w-full max-w-md">
//             <div className="flex justify-center">
//               <div className="animate-spin rounded-full h-12 w-12 border-y-2 border-gray-900" />
//             </div>
//             <p className="mt-4 text-center text-gray-600">Checking authentication status...</p>

//             <div className="mt-6 text-center space-x-4">
//               <button
//                 onClick={() => setLoadingTimeout(true)}
//                 className="text-blue-600 underline text-sm hover:text-blue-700"
//               >
//                 Show login form anyway
//               </button>
//               <button
//                 onClick={handleEmergencyReset}
//                 className="text-red-600 underline text-sm hover:text-red-700"
//               >
//                 Reset auth state
//               </button>
//             </div>
//           </div>
//         </div>
//         <SiteFooter />
//       </>
//     );
//   }

//   // Redirecting state
//   if (redirecting) {
//     return (
//       <>
//         <SiteHeader hideGetStarted />
//         <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
//           <div className="w-full max-w-md">
//             <div className="flex justify-center">
//               <div className="animate-spin rounded-full h-12 w-12 border-y-2 border-gray-900" />
//             </div>
//             <p className="mt-4 text-center text-gray-600">Redirecting...</p>
//           </div>
//         </div>
//         <SiteFooter />
//       </>
//     );
//   }

//   // Login form
//   return (
//     <>
//       <SiteHeader hideGetStarted />
//       <div className="min-h-screen bg-gray-50">
//         {(isLoading || storeIsLoading) && (
//           <div className="fixed top-2 right-2 bg-yellow-100 text-yellow-800 text-xs py-1 px-2 rounded border border-yellow-300">
//             Auth still loading - form may not work correctly
//           </div>
//         )}

//         <div className="max-w-md mx-auto px-4 py-12 sm:px-6 lg:px-8">
//           {/* Header Section */}
//           <div className="text-center mb-8">
//             <div className="inline-flex items-center gap-2 bg-beige-dark rounded-full px-6 py-2 mb-6 border border-gray-300 shadow-inner">
//               <span className="text-xs font-mono uppercase tracking-wider text-gray-800">SIGN IN</span>
//               <div className="w-2 h-2 rounded-full bg-gray-800"></div>
//             </div>

//             <h1 className="text-3xl font-bold text-gray-900 mb-2">
//               Welcome back
//             </h1>
//             <p className="text-lg text-gray-600">
//               Sign in to access your AI workflows
//             </p>
//           </div>

//           {error && (
//             <div className="mb-6">
//               <div className={`${
//                 error.includes('successfully signed out') || error.includes('has been reset successfully')
//                   ? 'bg-green-50 text-green-700 border border-green-200'
//                   : 'bg-red-50 text-red-700 border border-red-200'
//               } p-3 rounded-lg text-sm`}>
//                 {error}
//               </div>
//             </div>
//           )}

//           <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
//             <Suspense fallback={
//               <div className="text-center py-8">
//                 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
//                 <p className="mt-2 text-sm text-gray-500">Loading...</p>
//               </div>
//             }>
//               <AuthForm view="sign_in" />
//             </Suspense>
//           </div>

//           {/* Additional Links */}
//           <div className="mt-6 text-center">
//             <p className="text-sm text-gray-600">
//               Don't have an account?{' '}
//               <a href="/signup" className="font-medium text-blue-600 hover:text-blue-700">
//                 Sign up for free
//               </a>
//             </p>
//           </div>
//         </div>
//       </div>
//       <SiteFooter />
//     </>
//   );
// }

// export default function LoginPage() {
//   return (
//     <Suspense fallback={
//       <>
//         <SiteHeader hideGetStarted />
//         <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
//           <div className="flex justify-center">
//             <div className="animate-spin rounded-full h-12 w-12 border-y-2 border-gray-900" />
//           </div>
//           <p className="mt-4 text-center text-gray-600">Loading...</p>
//         </div>
//         <SiteFooter />
//       </>
//     }>
//       <LoginPageContent />
//     </Suspense>
//   );
// }
